import { z } from 'zod';

import { HumanTaskConditionSchema } from './conditional.js';
import {
  BlockValueFieldSchema,
  BlockValueFieldSchemaVariant,
} from '../base/blockValueFieldSchema.js';
import { BlockValueTemplateHint, validateBlockValueTemplate } from '../base/template.js';

export enum HumanTaskBlockFieldType {
  ContentMarkdown = 'ContentMarkdown',
  ReadOnlyValue = 'ReadOnlyValue',
  EditableValue = 'EditableValue',
  ImageUpload = 'ImageUpload',
  FileUpload = 'FileUpload',
  DateOnlyValue = 'DateOnlyValue',
  SingleOptionValue = 'SingleOptionValue',
  MultiOptionValue = 'MultiOptionValue',
  FileReferenceDownload = 'FileReferenceDownload',
}
export const HumanTaskReadOnlyFields = new Set([
  HumanTaskBlockFieldType.ContentMarkdown,
  HumanTaskBlockFieldType.ReadOnlyValue,
  HumanTaskBlockFieldType.FileReferenceDownload,
]);

const VariableNameFieldSchema = z.string().superRefine((value, context) => {
  const flowTemplateValidation = validateBlockValueTemplate(value);
  if (flowTemplateValidation.hint === BlockValueTemplateHint.Valid) {
    return;
  }
  if ('message' in flowTemplateValidation) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: flowTemplateValidation.message,
    });
  }
  if (flowTemplateValidation.hint === BlockValueTemplateHint.NotTemplateLike) {
    if (!/^[a-zA-Z]\w*$/.test(value)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Must start with a letter, followed by letters, numbers, or underscores. e.g. variableName',
      });
    }
  }
});

export const BaseHumanTaskFieldSchema = z.object({
  id: z.string(),
  isVisibleConditions: z.array(HumanTaskConditionSchema).optional(),
});

export const BaseEditableHumanTaskFieldSchema = BaseHumanTaskFieldSchema.extend({
  isRequiredConditions: z.array(HumanTaskConditionSchema).optional(),
});

export const HumanTaskContentMarkdownFieldSchema = BaseHumanTaskFieldSchema.extend({
  fieldType: z.literal(HumanTaskBlockFieldType.ContentMarkdown),
  content: z.string(),
});
export type HumanTaskContentMarkdownField = z.infer<typeof HumanTaskContentMarkdownFieldSchema>;

export const HumanTaskReadOnlyValueFieldSchema = BaseHumanTaskFieldSchema.extend({
  fieldType: z.literal(HumanTaskBlockFieldType.ReadOnlyValue),
  label: z.string(),
  value: z.string(),
  helperText: z.string().optional(),
});
export type HumanTaskReadOnlyValueField = z.infer<typeof HumanTaskReadOnlyValueFieldSchema>;

export const HumanTaskEditableValueFieldSchema = BaseEditableHumanTaskFieldSchema.extend({
  fieldType: z.literal(HumanTaskBlockFieldType.EditableValue),
  label: z.string(),
  variableName: VariableNameFieldSchema,
  initialValue: z.string(),
  helperText: z.string().optional(),
});
export type HumanTaskEditableValueField = z.infer<typeof HumanTaskEditableValueFieldSchema>;

export const HumanTaskImageUploadFieldSchema = BaseEditableHumanTaskFieldSchema.extend({
  fieldType: z.literal(HumanTaskBlockFieldType.ImageUpload),
  label: z.string(),
  variableName: VariableNameFieldSchema,
  helperText: z.string().optional(),
});
export type HumanTaskImageUploadField = z.infer<typeof HumanTaskImageUploadFieldSchema>;

export const HumanTaskFileUploadFieldSchema = BaseEditableHumanTaskFieldSchema.extend({
  fieldType: z.literal(HumanTaskBlockFieldType.FileUpload),
  label: z.string(),
  variableName: VariableNameFieldSchema,
  helperText: z.string().optional(),
});
export type HumanTaskFileUploadField = z.infer<typeof HumanTaskFileUploadFieldSchema>;

export enum HumanTaskFieldOptionsVariant {
  Static = 'Static',
  Dynamic = 'Dynamic',
}

export const HumanTaskFieldOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.string().optional(),
});
export type HumanTaskFieldOption = z.infer<typeof HumanTaskFieldOptionSchema>;

export const HumanTaskDateOnlyValueFieldSchema = BaseEditableHumanTaskFieldSchema.extend({
  fieldType: z.literal(HumanTaskBlockFieldType.DateOnlyValue),
  label: z.string(),
  variableName: VariableNameFieldSchema,
  initialValue: z.string(),
  helperText: z.string().optional(),
});
export type HumanTaskDateOnlyValueField = z.infer<typeof HumanTaskDateOnlyValueFieldSchema>;

export const HumanTaskSingleOptionValueFieldSchema = BaseEditableHumanTaskFieldSchema.extend({
  fieldType: z.literal(HumanTaskBlockFieldType.SingleOptionValue),
  label: z.string(),
  optionsVariant: z
    .enum(HumanTaskFieldOptionsVariant)
    .optional()
    .default(HumanTaskFieldOptionsVariant.Static),
  options: z.array(HumanTaskFieldOptionSchema),
  dynamicOptions: BlockValueFieldSchema({
    variant: BlockValueFieldSchemaVariant.TemplateAndPlainString,
  }).default(''),
  variableName: VariableNameFieldSchema,
  initialValue: z.string(),
});
export type HumanTaskSingleOptionValueField = z.infer<typeof HumanTaskSingleOptionValueFieldSchema>;

export const HumanTaskMultiOptionValueFieldSchema = BaseEditableHumanTaskFieldSchema.extend({
  fieldType: z.literal(HumanTaskBlockFieldType.MultiOptionValue),
  label: z.string(),
  optionsVariant: z
    .enum(HumanTaskFieldOptionsVariant)
    .optional()
    .default(HumanTaskFieldOptionsVariant.Static),
  options: z.array(HumanTaskFieldOptionSchema),
  dynamicOptions: BlockValueFieldSchema({
    variant: BlockValueFieldSchemaVariant.TemplateAndPlainString,
  }).default(''),
  variableName: VariableNameFieldSchema,
  initialValue: BlockValueFieldSchema({
    variant: BlockValueFieldSchemaVariant.OptionalTemplate,
  }),
});
export type HumanTaskMultiOptionValueField = z.infer<typeof HumanTaskMultiOptionValueFieldSchema>;

export const HumanTaskFileReferenceDownloadFieldSchema = BaseEditableHumanTaskFieldSchema.extend({
  fieldType: z.literal(HumanTaskBlockFieldType.FileReferenceDownload),
  id: z.string(),
  label: z.string(),
  value: z.string(),
});
export type HumanTaskFileReferenceDownloadField = z.infer<
  typeof HumanTaskFileReferenceDownloadFieldSchema
>;

export const HumanTaskFieldSchema = z.discriminatedUnion('fieldType', [
  HumanTaskContentMarkdownFieldSchema,
  HumanTaskReadOnlyValueFieldSchema,
  HumanTaskEditableValueFieldSchema,
  HumanTaskImageUploadFieldSchema,
  HumanTaskFileUploadFieldSchema,
  HumanTaskDateOnlyValueFieldSchema,
  HumanTaskSingleOptionValueFieldSchema,
  HumanTaskMultiOptionValueFieldSchema,
  HumanTaskFileReferenceDownloadFieldSchema,
]);
export type HumanTaskField = z.infer<typeof HumanTaskFieldSchema>;

export const HumanTaskStepSchema = z.object({
  id: z.string(),
  title: z.string(),
  fields: z.array(HumanTaskFieldSchema),
  isVisibleConditions: z.array(HumanTaskConditionSchema).optional(),
});
export type HumanTaskStep = z.infer<typeof HumanTaskStepSchema>;
